import { CheckIcon } from '@phosphor-icons/react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SubscriptionPlan } from '@cribbage/protocol';
import { LeanTag } from '~/table/classic/LeanTag.js';
import { type BillingInterval, changePlan, startCheckout } from '~/session/commerce.js';
import { useAuthStore } from '~/store/authStore.js';
import { ButtonLink } from '~/ui/Button.js';
import { ChipGroup } from '~/ui/ChipGroup.js';
import { MenuTile } from '~/ui/MenuTile.js';
import { Panel } from '~/ui/Panel.js';
import { Section } from '~/ui/Section.js';
import { useArrangement } from '~/ui/useArrangement.js';
import { Outcome, Price, PurchasesBand } from './SubscriptionPage.js';
import {
  CALL_FAILED,
  FOCUS,
  HEADLINE,
  PLANS,
  SUBSCRIPTION_PATH,
  type ShownPrice,
  type SubscriptionState,
  billedCents,
  billedOf,
  cheapest,
  confirmedMember,
  defaultInterval,
  formatPrice,
  intervalOf,
  longDate,
  onward,
  priceOf,
  signInFirst,
  standingOf,
  storeOf,
  subscriptionOf,
  subscriptionPath,
  useBillingStore,
  useStoreCall,
  wayOut,
} from './subscriptionPages.js';

/**
 * The upgrade cycle: the offer, the plans, the page after the store says yes,
 * and a member's Change plan. Drawn to the `lobby-offer` and
 * `lobby-subscription-settings` sketches and `docs/ux/commerce/paywall.md`.
 *
 * Every purchase goes through `session/commerce.ts`. A store's yes is not
 * enough to call a plan owned: the page reads `/me` again, and moves on to
 * Subscribed only when `/me` shows a member.
 */

const OFFER_LINES = [
  'hintsPeople',
  'hintsBots',
  'reviews',
  'avatars',
  'reactions',
  'goods',
] as const;

/**
 * The offer, `commerce.paywall`. One screen for every door that opens it. The
 * frame draws its title and one line under it; this is the body: what a member
 * gets, the price line, the one lemon, and the Purchases band.
 *
 * The price line stays true for the device: the apps sell from the cheapest
 * monthly plan, and the web sells yearly only (`brain/decisions.md`
 * 2026-10-08), from the cheapest price `/me` says it sells. With no price to
 * name, the line is left out. A guest needs a free account to subscribe
 * (D29), so the lemon is Sign in, with the reason above it.
 */
export function OfferPage({ state }: { readonly state: SubscriptionState }): React.JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((auth) => auth.user);
  const standing = standingOf(user);
  const guest = standing === 'guest';
  const device = useBillingStore();
  const interval = defaultInterval(device);
  const from = cheapest(user, device, interval);
  return (
    <>
      <Section title={t('subscription.offer.whatYouGet')}>
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {OFFER_LINES.map((line) => (
            <li key={line} className="flex items-center gap-3">
              <CheckIcon
                size={20}
                weight="bold"
                className="shrink-0 text-teal"
                aria-hidden="true"
              />
              <span className="text-ink">{t(`subscription.offer.${line}`)}</span>
            </li>
          ))}
        </ul>
        {from !== null && (
          <p className="mt-5 border-t border-gray-300 pt-4 text-center text-ink">
            {t(`subscription.offer.fromPrice_${interval}`, { price: formatPrice(from) })}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-3">
          {guest ? (
            <>
              <p className="text-center text-sm text-muted">{t('subscription.guestLine')}</p>
              {/* Signed in, the guest comes back to this offer, its way out kept. */}
              <ButtonLink
                variant="primary"
                {...signInFirst(subscriptionPath('offer'), onward(state))}
              >
                {t('auth.signIn')}
              </ButtonLink>
            </>
          ) : (
            // A member holds one plan at a time, so for one the lemon changes it.
            <ButtonLink
              variant="primary"
              to={subscriptionPath(standing === 'renewing' ? 'change-plan' : 'plans')}
              state={onward(state)}
              data-focus-key={FOCUS.subscribe}
            >
              {t('subscription.offer.subscribe')}
            </ButtonLink>
          )}
        </div>
      </Section>
      <PurchasesBand guest={guest} from="offer" state={state} />
    </>
  );
}

/**
 * Monthly or Yearly, on the apps only. The web sells yearly and shows no
 * switch (`brain/decisions.md` 2026-10-08).
 */
function IntervalChoice({
  value,
  onChange,
}: {
  readonly value: BillingInterval;
  readonly onChange: (next: BillingInterval) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (useBillingStore() === 'web') return null;
  return (
    <ChipGroup
      className="items-center"
      size="sm"
      label={t('subscription.plans.billing')}
      value={value}
      options={[
        { value: 'month', label: t('subscription.plans.monthly') },
        { value: 'year', label: t('subscription.plans.yearly') },
      ]}
      onChange={(next) => {
        onChange(next === 'year' ? 'year' : 'month');
      }}
    />
  );
}

/**
 * Four doors in the Lobby - Home set, one suit each, the price in each door's
 * trailing slot so the four stand in one column at the right edge. The bundle
 * is the lemon door and wears Most popular on its top-right corner. On Change
 * plan the lemon moves to the member's plan, and a Current tag on its top-left
 * corner says so in words (Mitch, 2026-09-21). The current door goes nowhere.
 *
 * A door is a link to this same page that chooses instead of navigating, as
 * the sketch draws it: `MenuTile` is always a link. While a choice is out the
 * doors take no second press, and the line under them says so in words
 * (`Outcome`): a link cannot be disabled, and a door drawn grey would read as
 * a plan that is not for sale.
 *
 * Each door wears the price the device sells it at (`priceOf`), the same one
 * the store charges. A plan with no price is not for sale here, so it has no
 * door, unless it is the member's own plan.
 */
function PlanDoors({
  interval,
  current,
  busy,
  priceFor,
  onChoose,
}: {
  readonly interval: BillingInterval;
  readonly current?: SubscriptionPlan | undefined;
  readonly busy: boolean;
  readonly priceFor: (plan: SubscriptionPlan) => ShownPrice | null;
  readonly onChoose: (plan: SubscriptionPlan) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const small = useArrangement() === 'phone';
  const doors = PLANS.flatMap(({ id, suit }) => {
    const price = priceFor(id);
    return price === null && id !== current ? [] : [{ id, suit, price }];
  });
  return (
    <ul className="m-0 flex list-none flex-col gap-4 p-0">
      {doors.map(({ id, suit, price }) => (
        <li key={id} className="relative">
          {id === 'bundle' && (
            <LeanTag placement="pinned" small={small} label={t('subscription.plans.mostPopular')} />
          )}
          {id === current && (
            // Most popular's tag, mirrored to the top-left corner so the two
            // never collide when the bundle is the current plan.
            <LeanTag
              placement="pinned-start"
              small={small}
              label={t('subscription.changePlan.current')}
            />
          )}
          <MenuTile
            to={pathname}
            variant={id === (current ?? 'bundle') ? 'primary' : 'default'}
            suit={suit}
            label={t(`subscription.plans.${id}`)}
            hint={t(`subscription.plans.${id}Line`)}
            trailing={
              price === null ? undefined : <Price cents={price.cents} interval={interval} />
            }
            aria-current={id === current ? 'true' : undefined}
            onClick={(event) => {
              event.preventDefault();
              if (id !== current && !busy) onChoose(id);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * Choose a plan. Choosing hands off to the store (D28) through `startCheckout`.
 * A yes that `/me` confirms opens Subscribed; anything else is one line under
 * the doors, and the doors stay for another try. A guest reached by link is
 * told an account comes first, and a door opens Sign in, which comes back here.
 */
export function PlansPage({ state }: { readonly state: SubscriptionState }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const device = useBillingStore();
  const [interval, setCadence] = useState(defaultInterval(device));
  const user = useAuthStore((auth) => auth.user);
  const guest = standingOf(user) === 'guest';
  const checkout = useStoreCall();
  const { outcome } = checkout;
  const priceFor = (plan: SubscriptionPlan): ShownPrice | null =>
    priceOf(user, device, plan, interval);

  const choose = (plan: SubscriptionPlan): void => {
    // A guest needs an account first (D29), and comes back to these plans after.
    if (guest) {
      const signIn = signInFirst(subscriptionPath('plans'), onward(state));
      void navigate(signIn.to, { state: signIn.state });
      return;
    }
    checkout.run(
      () => startCheckout(plan, interval, priceFor(plan)?.id),
      CALL_FAILED,
      async (result, ours) => {
        if (result.status === 'repriced') {
          // The doors redraw at the new price once `/me` is read again.
          await useAuthStore.getState().refresh();
          if (ours()) checkout.show('repriced');
          return;
        }
        if (result.status !== 'verified') {
          checkout.show(result.status);
          return;
        }
        const member = await confirmedMember();
        if (!ours()) return;
        if (member) void navigate(subscriptionPath('subscribed'), { state: onward(state) });
        // The store said yes and `/me` does not show it yet: that is pending.
        else checkout.show('pending');
      },
    );
  };

  return (
    <>
      <IntervalChoice
        value={interval}
        onChange={(next) => {
          setCadence(next);
          checkout.show(null);
        }}
      />
      {guest && <p className="text-center text-muted">{t('subscription.guestLine')}</p>}
      <PlanDoors interval={interval} busy={checkout.busy} priceFor={priceFor} onChoose={choose} />
      <Outcome
        className="text-center"
        busy={checkout.busy}
        text={
          outcome === null
            ? null
            : t(`subscription.result.checkout.${outcome === 'verified' ? 'pending' : outcome}`)
        }
      />
    </>
  );
}

/**
 * Change plan: the same doors with the member's plan current. One plan at a
 * time (Mitch, 2026-09-21), so any other door replaces it, through the store
 * that bills it. A change `/me` confirms returns to Subscription, which then
 * names the new plan, or the date it starts. The current door wears what the
 * member pays, which may be a price since replaced; every other door wears
 * the price a change to it charges.
 */
export function ChangePlanPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((auth) => auth.user);
  const view = subscriptionOf(user);
  const subscription = view === null ? null : billedOf(view);
  const device = useBillingStore();
  const held = subscription === null ? 'month' : intervalOf(subscription);
  // The web sells yearly only; the apps open on the interval the member pays.
  const [interval, setCadence] = useState<BillingInterval>(device === 'web' ? 'year' : held);
  const change = useStoreCall();
  const { outcome } = change;
  const current = subscription !== null && held === interval ? subscription.plan : undefined;
  const priceFor = (plan: SubscriptionPlan): ShownPrice | null =>
    plan === current && subscription !== null
      ? { cents: billedCents(subscription) }
      : priceOf(user, device, plan, interval);

  const choose = (plan: SubscriptionPlan): void => {
    change.run(
      () => changePlan(plan, interval, priceFor(plan)?.id),
      CALL_FAILED,
      async (result, ours) => {
        if (result.status === 'repriced') {
          // The doors redraw at the new price once `/me` is read again.
          await useAuthStore.getState().refresh();
          if (ours()) change.show('repriced');
          return;
        }
        if (result.status !== 'verified') {
          change.show(result.status);
          return;
        }
        await useAuthStore.getState().refresh();
        if (ours()) void navigate(SUBSCRIPTION_PATH, { state: { returnTo: FOCUS.manage } });
      },
    );
  };

  return (
    <>
      <IntervalChoice
        value={interval}
        onChange={(next) => {
          setCadence(next);
          change.show(null);
        }}
      />
      <PlanDoors
        interval={interval}
        current={current}
        busy={change.busy}
        priceFor={priceFor}
        onChoose={choose}
      />
      <p className="text-center text-muted">{t('subscription.changePlan.intro')}</p>
      <Outcome
        className="text-center"
        busy={change.busy}
        text={
          outcome === null || outcome === 'verified'
            ? null
            : t(`subscription.result.change.${outcome}`)
        }
      />
    </>
  );
}

/**
 * Verified success. A thank-you first, in the display face: the overline says
 * Thank you and, and the headline finishes the sentence. Then what is on,
 * named per plan, then Done back to what the player was doing
 * (`commerce.paywall`: verified success returns the origin). The next bill
 * date and that it repeats sit under Done, read off `/me`.
 *
 * A `Panel` rather than a `Section`, because the heading is centered and the
 * region's name is the headline, not an overline.
 */
export function SubscribedPage({
  state,
}: {
  readonly state: SubscriptionState;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const headline = useId();
  const user = useAuthStore((auth) => auth.user);
  const subscription = subscriptionOf(user);
  if (subscription === null) return null;
  const billed = billedOf(subscription);
  const renewing = standingOf(user) === 'renewing';
  return (
    <Panel aria-labelledby={headline}>
      <div className="flex flex-col items-center gap-3 text-center">
        <p className="text-xs font-extrabold tracking-widest text-gray-700 uppercase">
          {t('subscription.subscribed.thanks')}
        </p>
        <h2 id={headline} className={HEADLINE}>
          {t('subscription.subscribed.headline')}
        </h2>
        <p className="text-ink">{t(`subscription.subscribed.on_${subscription.plan}`)}</p>
      </div>
      <div className="mt-2 flex flex-col gap-3">
        <ButtonLink
          variant="primary"
          to={wayOut(state).to}
          state={{ returnTo: wayOut(state).returnTo }}
        >
          {t('subscription.subscribed.done')}
        </ButtonLink>
      </div>
      {renewing && billed !== null && (
        <>
          <p className="mt-2 border-t border-gray-300 pt-5 text-center text-sm text-muted">
            {t('subscription.subscribed.nextBill', {
              date: longDate(subscription.current_period_ends_at, i18n.language),
            })}
          </p>
          <p className="text-center text-sm text-muted">
            {t(`subscription.subscribed.renews_${intervalOf(subscription)}`, {
              store: t(`subscription.store.${storeOf(billed)}`),
            })}
          </p>
        </>
      )}
    </Panel>
  );
}
